import type { ReactNode } from 'react';
import type { UserLocalStorage } from '../../../../types/localStorage';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import { getTestProps } from '../../../../lib/helpers';

import { withForm } from '../../../../form-hooks/form';

export const MESSAGE_FIELDS = [
  'fieldIsInvalid',
  'fieldIsRequired',
  'chooseFiles',
  'sendText',
  'formIsNotConfiguredProperly',
  'formHasBeenSent',
  'chooseOptionText',
  'fileUploadInfo',
  'fileUploadDuplicateFileErrorMessage',
  'fileTooBig',
  'fileWrongFormat',
  'maxFilesReachedText',
  'maxUploadLimitReachedText',
];

const RECAPTCHA_FIELDS = ['recaptchaPublic', 'recaptchaPrivate'];

const AdvancedBox = ({
  title,
  description,
  children,
  withDivider = true,
}: {
  title: string;
  description: string;
  children: ReactNode;
  withDivider?: boolean;
}) => (
  <>
    <p className="font-bold">{title}</p>
    <p>{description}</p>
    <div className="space-y-5 mt-5">{children}</div>
    {withDivider && (
      <div className="h-0 border-t border-slate-200  dark:border-slate-800 w-full my-10" />
    )}
  </>
);

const Advanced = withForm({
  defaultValues: {
    origin: '*',
    flotiqApiKey: '',
    config: {},
    messages: {},
  },
  props: {
    testId: '',
  },
  render: function Render({ form, testId }) {
    const { t } = useTranslation();
    const [user] = useLocalStorageState<UserLocalStorage>('cms.user');

    const mimetypesOptions = useMemo(
      () =>
        user?.data?.allowedMimetypes?.split(',')?.map((value) => ({
          value: value,
          label: value,
        })) || [],
      [user?.data?.allowedMimetypes],
    );

    return (
      <>
        <AdvancedBox
          title={t('ContentTypeForm.FormGenerator.ReCaptcha')}
          description={t('ContentTypeForm.FormGenerator.ReCaptchaDescription')}
        >
          {RECAPTCHA_FIELDS.map((name) => (
            <form.AppField key={name} name={`config.${name}`}>
              {(subField) => (
                <subField.Input
                  label={t(`ContentTypeForm.FormGenerator.Field.${name}`)}
                  {...getTestProps(testId, `recaptcha-${name}`, 'testId')}
                />
              )}
            </form.AppField>
          ))}
        </AdvancedBox>

        <AdvancedBox
          title={t('ContentTypeForm.FormGenerator.FileUploads')}
          description={t(
            'ContentTypeForm.FormGenerator.FileUploadsDescription',
          )}
        >
          <form.AppField name={`config.maxFiles`}>
            {(subField) => (
              <subField.Input
                label={t(`ContentTypeForm.FormGenerator.Field.maxFiles`)}
                type="number"
              />
            )}
          </form.AppField>

          <form.AppField name="config.allowedTypes">
            {(subField) => (
              <subField.Dropdown
                label={t('ContentTypeForm.FormGenerator.Field.allowedTypes')}
                options={mimetypesOptions}
                helpText={t(
                  'ContentTypeForm.FormGenerator.HelpText.allowedTypes',
                )}
                multiple
              />
            )}
          </form.AppField>
        </AdvancedBox>

        <AdvancedBox
          title={t('ContentTypeForm.FormGenerator.Messages')}
          description={t('ContentTypeForm.FormGenerator.MessagesDescription')}
          withDivider={false}
        >
          {MESSAGE_FIELDS.map((name) => (
            <form.AppField key={name} name={`messages.${name}`}>
              {(subField) => (
                <subField.Input
                  label={t(`ContentTypeForm.FormGenerator.Field.${name}`)}
                  helpText={`${t('ContentTypeForm.FormGenerator.Default')}: ${t(
                    `ContentTypeForm.FormGenerator.HelpText.${name}`,
                  )}`}
                  {...getTestProps(testId, `messages-${name}`, 'testId')}
                />
              )}
            </form.AppField>
          ))}
        </AdvancedBox>
      </>
    );
  },
});

export default Advanced;
